import { PlusCircleIcon } from '@heroicons/react/24/solid';
import { Button } from '@praetorian-chariot/ui';
import React from 'react';

import { AgentsIcon } from '@/components/icons';
import type { NoDataProps } from '@/components/ui/NoData';
import { createClearFiltersButton } from '@/components/ui/utils/createClearFiltersButton';

/**
 * No-data configuration for agents table
 *
 * Returns appropriate NoData config based on state:
 * 1. Error state - Connection issue with retry/setup actions
 * 2. Empty state (isAllEmpty) - No agents configured yet
 * 3. Filtered empty - No agents match current filter
 */
export const getNoDataConfig = (
  isAllEmpty: boolean,
  hasError: boolean,
  onNewAgent: () => void,
  onRetry?: () => void,
  onClearFilters?: () => void
): NoDataProps => {
  // Error State (highest priority) - uses friendly icon like empty state
  if (hasError) {
    return {
      icon: <AgentsIcon className="text-chariot-stroke size-20" />,
      title: 'Unable to Connect',
      description:
        'No agents identified. Please add your first agent or check your connection.',
      action: (
        <div className="flex gap-3">
          {onRetry && (
            <Button
              label="Retry"
              onClick={onRetry}
              styleType="secondary"
              size="sm"
            />
          )}
          <Button
            label="Add Agent"
            onClick={onNewAgent}
            styleType="primary"
            size="sm"
          />
        </div>
      ),
    };
  }

  // All Empty State - No agents connected
  if (isAllEmpty) {
    return {
      icon: <AgentsIcon className="text-chariot-stroke size-20" />,
      title: 'No Agents Connected',
      description:
        'Connect your first agent to start monitoring your internal intranet attack surface',
      action: (
        <Button
          styleType="primary"
          startIcon={<PlusCircleIcon className="size-4" />}
          label="Add Agent"
          size="sm"
          onClick={onNewAgent}
          className="px-4"
        />
      ),
    };
  }

  // Filtered Empty State - Search/filter returned no results
  return {
    icon: <AgentsIcon className="text-chariot-stroke size-20" />,
    title: 'No Agents match your current filter',
    action: createClearFiltersButton({
      hasActiveFilters: !!onClearFilters,
      onClearFilters: onClearFilters || (() => {}),
    }),
  };
};
